<template>
    <div class="conversation-dialog">
        <div id="agent-model-overlay" class="conversation-overlay" :class="{ active: expandedId !== null }"></div>
        <div class="conversation-history" :inert="expandedId !== null">
            <div
                ref="history"
                class="conversation-messages"
                role="log"
                aria-label="Conversation"
                aria-live="polite"
                @scroll.passive="updateActiveMessage">
                <div class="conversation-messages-content">
                    <div
                        v-for="turn in turns"
                        :key="turn.id"
                        class="conversation-turn">
                        <MessageUser :message="turn.user" :data-message-id="turn.user.id" />
                        <MessageAgent
                            v-for="reply in turn.replies"
                            :key="reply.id"
                            :message="reply"
                            :busy="busy"
                            :checking="checking"
                            :expanded="expandedId === reply.id"
                            @retry="$emit('retry', reply.id)"
                            @check-status="$emit('check-status', reply.id)"
                            @expand="$emit('expand', expandedId === reply.id ? null : reply.id)" />
                    </div>
                </div>
            </div>

            <ConversationNavigation
                :messages="navigationMessages"
                :active-message-id="activeMessageId"
                @select="scrollToMessage" />
        </div>

        <div class="conversation-composer" :inert="expandedId !== null">
            <PromptInput
                class="conversation-composer-input"
                :model-value="modelValue"
                :attachments="attachments"
                :busy="busy"
                @update:model-value="$emit('update:modelValue', $event)"
                @add-files="$emit('add-files', $event)"
                @remove-file="$emit('remove-file', $event)"
                @submit="$emit('submit')" />
        </div>

        <v-btn
            class="conversation-new"
            color="color1"
            variant="tonal"
            elevation="0"
            aria-label="Create new conversation"
            :disabled="busy"
            :inert="expandedId !== null"
            @click="$emit('new-conversation')">
            <v-icon icon="fa-solid fa-plus" />
            <v-tooltip :disabled="$vuetify.display.xs || $vuetify.display.platform.touch" activator="parent" location="start">Create new conversation</v-tooltip>
        </v-btn>
    </div>
</template>

<script>
import MessageUser from './MessageUser.vue'
import MessageAgent from './MessageAgent.vue'
import ConversationNavigation from './ConversationNavigation.vue'
import PromptInput from '../prompt/PromptInput.vue'
import { markRaw } from 'vue'
import { ViewerSession } from '../viewer/ViewerSession.js'

export default {
    name: 'ConversationDialog',
    components: { MessageUser, MessageAgent, ConversationNavigation, PromptInput },
    props: {
        modelValue: { type: String, default: '' },
        busy: { type: Boolean, default: false },
        checking: { type: Boolean, default: false },
        expandedId: { type: Number, default: null },
        openedMessageId: { type: Number, default: null },
        messages: { type: Array, default: () => [] },
        attachments: { type: Array, default: () => [] }
    },
    emits: ['update:modelValue', 'add-files', 'remove-file', 'submit', 'new-conversation', 'retry', 'check-status', 'expand'],
    data() {
        return { activeMessageId: null, savedScrollTop: 0, viewerSession: markRaw(new ViewerSession()) }
    },
    provide() {
        return { agentViewerSession: this.viewerSession }
    },
    computed: {
        preferredViewerId() {
            if (this.expandedId !== null) return this.expandedId
            return this.messages.filter(message => message.role !== 'user').at(-1)?.id ?? null
        },
        navigationMessages() {
            return this.turns.flatMap(turn => [turn.user, ...turn.replies])
        },
        turns() {
            const turns = []
            for (const message of this.messages) {
                if (message.role === 'user') {
                    turns.push({ id: message.id, user: message, replies: [] })
                }
                else {
                    const turn = turns.find(turn => turn.id === message.inputId) || turns.at(-1)
                    turn?.replies.push(message)
                }
            }
            return turns
        }
    },
    watch: {
        preferredViewerId: {
            immediate: true,
            handler(id) { this.viewerSession.prefer(id, this.expandedId !== null) }
        },
        expandedId(id, previous) {
            this.viewerSession.lockedId = id
            if (id !== null) this.viewerSession.activate(id)
            if (id !== null && previous === null) this.savedScrollTop = this.$refs.history.scrollTop
            if (id === null) this.$nextTick(() => { this.$refs.history.scrollTop = this.savedScrollTop })
        },
        'messages.length'() {
            const latest = this.messages.at(-1)
            this.$nextTick(() => this.scrollToMessage(latest?.id))
        },
        openedMessageId(id) {
            if (id !== null) this.$nextTick(() => this.scrollToMessage(id))
        }
    },
    methods: {
        handleKeydown(event) {
            if (event.key === 'Escape' && this.expandedId !== null) this.$emit('expand', null)
        },
        showLatestMessage() {
            this.$nextTick(() => {
                const history = this.$refs.history
                history.scrollTop = history.scrollHeight
                this.activeMessageId = this.navigationMessages.at(-1)?.id ?? null
            })
        },
        scrollToMessage(id) {
            const history = this.$refs.history
            const message = history.querySelector(`[data-message-id="${id}"]`)
            if (!message) return

            history.scrollTop += message.getBoundingClientRect().top - history.getBoundingClientRect().top - 24
            this.activeMessageId = id
        },
        updateActiveMessage() {
            if (this.expandedId !== null) return
            const history = this.$refs.history
            if (history.scrollHeight > history.clientHeight && history.scrollTop + history.clientHeight >= history.scrollHeight - 2) {
                this.activeMessageId = this.navigationMessages.at(-1)?.id ?? null
                return
            }

            const top = history.getBoundingClientRect().top + 24
            let closestDistance = Infinity
            for (const message of history.querySelectorAll('[data-message-id]')) {
                const distance = Math.abs(message.getBoundingClientRect().top - top)
                if (distance < closestDistance) {
                    closestDistance = distance
                    this.activeMessageId = Number(message.dataset.messageId)
                }
            }
        }
    },
    mounted() {
        if (this.openedMessageId !== null) this.$nextTick(() => this.scrollToMessage(this.openedMessageId))
        else this.showLatestMessage()
        window.addEventListener('keydown', this.handleKeydown)
    },
    beforeUnmount() {
        this.viewerSession.dispose()
        window.removeEventListener('keydown', this.handleKeydown)
    }
}
</script>

<style scoped>
.conversation-dialog {
    --conversation-rail-width: 70px;
    --conversation-fade-height: 48px;
    position: relative;
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.conversation-history {
    flex: 1;
    min-height: 0;
    display: flex;
}

.conversation-overlay {
    position: absolute;
    inset: 0;
    z-index: 4;
    pointer-events: none;
}

.conversation-overlay.active {
    pointer-events: auto;
    background: rgb(var(--v-theme-darkbase));
}

.conversation-messages {
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    scrollbar-width: none;
    padding: 32px 0 calc(24px + var(--conversation-fade-height)) var(--conversation-rail-width);
    -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - var(--conversation-fade-height)), transparent);
    mask-image: linear-gradient(to bottom, #000 calc(100% - var(--conversation-fade-height)), transparent);
}

.conversation-messages::-webkit-scrollbar {
    display: none;
}

.conversation-messages-content {
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 100%;
    max-width: 936px;
    margin-inline: auto;
}

.conversation-turn {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    min-width: 0;
}

.conversation-composer {
    flex: none;
    padding: 16px var(--conversation-rail-width) 24px;
    background: rgb(var(--v-theme-darkbase));
}

.conversation-composer-input {
    max-width: 936px;
    margin-inline: auto;
}

.conversation-new {
    position: absolute;
    right: 8px;
    bottom: 24px;
    width: 54px;
    min-width: 54px;
    height: 54px;
    padding: 0;
}

@media (max-width: 1600px) {
    .conversation-dialog {
        --conversation-rail-width: 60px;
    }

    .conversation-new {
        right: 5px;
        width: 50px;
        min-width: 50px;
        height: 50px;
    }
}

@media (max-width: 1200px) {
    .conversation-dialog {
        --conversation-rail-width: 50px;
    }

    .conversation-new {
        width: 40px;
        min-width: 40px;
        height: 40px;
    }
}

@media (max-width: 600px) {
    .conversation-dialog {
        --conversation-fade-height: 32px;
    }

    .conversation-messages {
        padding: 24px 0 calc(16px + var(--conversation-fade-height)) 12px;
    }

    .conversation-messages-content {
        gap: 28px;
    }

    .conversation-composer {
        padding: 12px 12px 60px;
    }

    .conversation-new {
        right: 12px;
        bottom: 10px;
    }
}
</style>
